<template lang="pug">
.outing-table-component
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- ヘッダー：選択
    template(#head(select)="data")
      .d-flex.flex-column
        label(style="margin-left:6px;")
          input(v-model="_allSelect" type="checkbox")
          span.text
    //- ヘッダー：利用者
    template(#head(resident)="data")
      .resident-name 利用者名
      .room-name （部屋名）
    //- ヘッダー：申請者
    template(#head(applicant)="data")
      .name-relation 申請者（続柄）
      .applicant-pickup お迎えに来る方（続柄）

    //- セル：選択
    template(#cell(select)="row")
      label(v-if="row.item.consented == false && row.item.deleteFlag == false" style="margin-left:6px;")
        input(v-model="row.item.select" type="checkbox")
        span.text
    //- セル：種別
    template(#cell(type)="row")
      .type(v-if="row.item.type == 'goingOut'") 外出
      .type(v-if="row.item.type == 'stayingOut'") 外泊
    //- セル：入力
    template(#cell(inputBy)="row")
      .input-type(v-if="row.item.inputBy == 'family'") 本人
      .input-type(v-if="row.item.inputBy == 'facility'") 代理
    //- セル：状態
    template(#cell(status)="row")
      .status.status-no-consented(v-if="row.item.consented == false") 未承諾
      .status.status-consented(v-else) 承諾済
    //- セル：面会日時
    template(#cell(datetimes)="row")
      .d-flex.flex-column(v-if="row.item.type == 'goingOut'")
        .d-flex.flex-row
          span {{ getDate(row.item.startDatetime) }}（
          span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
          span {{ getTime(row.item.startDatetime) }}
          span ～
          span {{ getTime(row.item.endDatetime) }}
      .d-flex.flex-column(v-if="row.item.type == 'stayingOut'")
        .d-flex.flex-row
          span {{ getDate(row.item.startDatetime) }}（
          span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
          span {{ getTime(row.item.startDatetime) }}
          span ～
        .d-flex.flex-row
          span {{ getDate(row.item.endDatetime) }}（
          span(:class="weekCssClass(row.item.endDatetime)") {{ getWeek(row.item.endDatetime) }}）
          span {{ getTime(row.item.endDatetime) }}
    //- セル：利用者
    template(#cell(resident)="row")
      .resident {{ row.item.targetResidentInfo.name }}
      .room-name （{{ row.item.targetResidentInfo.roomName }}）
    //- セル：申請者
    template(#cell(applicant)="row")
      .d-flex.flex-row
        .family-name {{ row.item.applicantFamilyInfo.name }}
        .family-relation （{{ row.item.applicantFamilyInfo.relation }}）
      .d-flex.flex-row(v-if="row.item.applicantPickup")
        span 申請者と同じ
      .d-flex.flex-row(v-else)
        .applicant-pickup {{ row.item.pickupPerson.name }}（{{ row.item.pickupPerson.relation }}）
    //- セル：届け出内容
    template(#cell(detail)="row")
      button.btn.detail-btn(@click="onDetail(row.item)") 詳細
    //- セル：操作
    template(#cell(control)="row")
      .buttons(v-if="row.item.deleteFlag == false")
        .before-consented(v-if="!row.item.consented")
          button.btn.consented-btn.mr-2(@click="onConsented(row.item)") 承諾
          button.btn.refuse-btn(@click="onRefuse(row.item)") 辞退
        .after-consented(v-if="row.item.consented")
          button.btn.cancel-btn(@click="onCancel(row.item)") キャンセル
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { OutingGetModel } from "@/commons/services/Outings";

import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * ### 外出外泊届けテーブルコンポーネント
 */
@Component({
  components: {
    HelpIcon
  }
})
export default class OutingTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<OutingTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<OutingTableField>;
  /** 全体行数 */
  @Prop()
  totalRows!: number;
  /** 現在のページ */
  @Prop()
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop()
  perPage!: number;
  /** ローディング表示可否 */
  @Prop()
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** 全て選択列のチェック */
  @Prop()
  allSelect!: boolean;

  get _value(): Array<OutingTableItem> {
    return this.value;
  }

  get _allSelect(): boolean {
    return this.allSelect;
  }

  set _allSelect(newValue: boolean) {
    this.$emit("update:allSelect", newValue);
    this.$emit("onAllSelect");
    return;
  }

  /**
   * ### 時分を返す
   */
  public getTime(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "hh:MM");
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "mm月dd日");
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(date: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: OutingTableItem, filter: FilterSetting): boolean {
    return this.filterGroupId(item, filter) && this.filterSearchWords(item, filter) && this.filterDeleted(item, filter);
  }

  public filterGroupId(item: OutingTableItem, filter: FilterSetting): boolean {
    if (filter.id == "") {
      return true;
    }

    if (item.targetResidentInfo.groupId == Number(filter.id)) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(item: OutingTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundResidentName: boolean = true;
    let foundResidentKanaName: boolean = true;
    let foundRoom: boolean = true;
    let foundFamilyName: boolean = true;
    let foundPickupPerson: boolean = true;

    for (const word of words) {
      // 利用者名
      if (item.targetResidentInfo.name.indexOf(word) == -1) {
        foundResidentName = false;
      }

      // 利用者名（ふりがな）
      if (item.targetResidentInfo.kanaName) {
        if (item.targetResidentInfo.kanaName.indexOf(word) == -1) {
          foundResidentKanaName = false;
        }
      } else {
        foundResidentKanaName = false;
      }

      // 部屋名
      if (item.targetResidentInfo.roomName.indexOf(word) == -1) {
        foundRoom = false;
      }

      // 家族名
      if (item.applicantFamilyInfo.name.indexOf(word) == -1) {
        foundFamilyName = false;
      }

      // お迎え者
      if (!item.applicantPickup && item.pickupPerson?.name.indexOf(word) == -1) {
        foundPickupPerson = false;
      } else {
        foundPickupPerson = false;
      }
    }

    return foundResidentName || foundResidentKanaName || foundRoom || foundFamilyName || foundPickupPerson;
  }

  public filterDeleted(item: OutingTableItem, filter: FilterSetting): boolean {
    // 削除済を表示する場合、全レコードを表示させる
    if (filter.showCanceled == true) {
      return true;
    }
    // 削除済を表示しない場合、削除されていないレコードのみを表示させる
    else {
      if (item.deleteFlag == false) {
        return true;
      } else {
        return false;
      }
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 詳細ボタン押下イベント処理
   */
  public onDetail(item: OutingTableItem): void {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 承諾ボタン押下イベント処理
   */
  public onConsented(item: OutingTableItem): void {
    this.$emit("onConsented", item);
    return;
  }

  /**
   * ### 辞退ボタン押下イベント処理
   */
  public onRefuse(item: OutingTableItem): void {
    this.$emit("onRefuse", item);
    return;
  }

  /**
   * ### キャンセルボタン押下イベント処理
   */
  public onCancel(item: OutingTableItem): void {
    this.$emit("onCancel", item);
    return;
  }

  /**
   * ### 全て選択押下イベント
   */
  public onAllSelect(): void {
    this.$emit("onAllSelect");
    return;
  }
}

/** テーブルの列名の情報 */
export interface OutingTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OutingTableItem extends OutingGetModel {
  /** 一括承諾用の選択状態 */
  select: boolean;
  /** 行単位の設定値 */
  _rowVariant?: string;
}

export interface FilterSetting {
  id: string;
  searchWord: string;
  showCanceled: boolean;
}
</script>

<style lang="scss" scoped>
.outing-table-component {
  .detail-btn,
  .consented-btn,
  .refuse-btn {
    min-width: 57px;
  }
  .cancel-btn {
    min-width: 120px;
  }
}
</style>
